import React, { useState } from "react";
import {
  Banner,
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Collapsible,
  Grid,
  IndexTable,
  InlineStack,
  Popover,
  Text,
  TextField,
  Tooltip,
} from "@shopify/polaris";
import { ClipboardIcon, ExternalIcon } from "@shopify/polaris-icons";
import { Bot, ChevronDown, Edit2, Eye, Pencil, Search, Trash2 } from "lucide-react";
import type { KbGap, KbItem } from "../types";

type AssistantTabProps = {
  analyticsLoading: boolean;
  filteredKbItems: KbItem[];
  gapItemsPerPage: number;
  gapPage: number;
  handleDeleteKbItem: (id: string) => void;
  handleExportCSV: () => void;
  handleExportJSON: () => void;
  handleImportFile: React.ChangeEventHandler<HTMLInputElement>;
  isAgentPlus: boolean;
  isRefreshing: boolean;
  isRepPlus: boolean;
  kbGaps: KbGap[];
  kbItemsPerPage: number;
  kbPage: number;
  kbSearch: string;
  loading: boolean;
  onCopyMasterPrompt: () => void;
  onDeleteKbItem: (id: string) => void;
  onEditKbItem: (item: KbItem) => void;
  onViewKbItem: (item: KbItem) => void;
  onOpenNewTopicModal: () => void;
  onOpenPlansTab: () => void;
  onScanPage: () => void | Promise<void>;
  onSeedGreetings: () => void;
  onShowWipeModal: () => void;
  onTrainGap: (gap: KbGap) => void;
  paginatedGapItems: KbGap[];
  paginatedKbItems: KbItem[];
  scrapeUrl: string;
  setGapPage: React.Dispatch<React.SetStateAction<number>>;
  setKbPage: React.Dispatch<React.SetStateAction<number>>;
  setKbSearch: (value: string) => void;
  setScrapeUrl: (value: string) => void;
  setShowSetupGuide: React.Dispatch<React.SetStateAction<boolean>>;
  showPhase2Tiers: boolean;
  showSetupGuide: boolean;
  themeColor: string;
};

export function AssistantTab({
  analyticsLoading,
  filteredKbItems,
  gapItemsPerPage,
  gapPage,
  handleExportCSV,
  handleExportJSON,
  handleImportFile,
  isAgentPlus,
  isRefreshing,
  isRepPlus,
  kbGaps,
  kbItemsPerPage,
  kbPage,
  kbSearch,
  loading,
  onCopyMasterPrompt,
  onDeleteKbItem,
  onEditKbItem,
  onViewKbItem,
  onOpenNewTopicModal,
  onOpenPlansTab,
  onScanPage,
  onSeedGreetings,
  onShowWipeModal,
  onTrainGap,
  paginatedGapItems,
  paginatedKbItems,
  scrapeUrl,
  setGapPage,
  setKbPage,
  setKbSearch,
  setScrapeUrl,
  setShowSetupGuide,
  showPhase2Tiers,
  showSetupGuide,
  themeColor,
}: AssistantTabProps) {
  const [showExportPopover, setShowExportPopover] = useState(false);

  if (!isAgentPlus) {
    return (
      <Card>
        <BlockStack gap="400" align="center">
          <Bot size={48} className="theme-icon-muted-strong" style={{ margin: "0 auto" }} />
          <Text as="h2" variant="headingLg" alignment="center">
            Meet Your Smart Assistant
          </Text>
          <Text as="p" alignment="center" tone="subdued">
            Train a smart assistant on your store&apos;s content. Handle customer support automatically, 24/7.
          </Text>
          <div style={{ textAlign: "center" }}>
            <button
              onClick={onOpenPlansTab}
              style={{
                background: themeColor,
                border: "none",
                borderRadius: "10px",
                padding: "10px 16px",
                cursor: "pointer",
                color: "white",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
              }}
            >
              Upgrade to Reach Agent
            </button>
          </div>
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="500">
      <Card>
        <BlockStack gap="400">
          <div onClick={() => setShowSetupGuide((value) => !value)} style={{ cursor: "pointer" }}>
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  How to train your Chatbot
                </Text>
                <Badge tone="magic">Setup Guide</Badge>
              </InlineStack>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  setShowSetupGuide((value) => !value);
                }}
                aria-expanded={showSetupGuide}
                aria-controls="setup-guide-collapsible"
                className="theme-section-chevron"
              >
                <span
                  style={{
                    display: "flex",
                    transform: showSetupGuide ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <ChevronDown size={20} />
                </span>
              </button>
            </InlineStack>
          </div>

          <Collapsible
            open={showSetupGuide}
            id="setup-guide-collapsible"
            transition={{ duration: "150ms", timingFunction: "ease-in-out" }}
            expandOnPrint
          >
            <BlockStack gap="500">
              {/* ── Concept strip ── */}
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd" tone="subdued">
                  Before adding topics, it helps to understand how Reach picks an answer and how replies can guide customers to the next step.
                </Text>
              </BlockStack>

              <Grid>
                <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 4 }}>
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="148px">
                    <BlockStack gap="150">
                      <Text as="span" fontWeight="bold">🎯 Use Keywords People Actually Type</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Reach matches messages using your <strong>keywords</strong>, not just the topic title.
                        For a topic like <em>"Shipping Policy"</em>, add keywords people might actually type, like <em>shipping, delivery, where is my order, how long does shipping take</em>.
                        Include synonyms, common misspellings, typos, and alternate wording so more customer messages match the right topic.
                      </Text>
                    </BlockStack>
                  </Box>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 4 }}>
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="148px">
                    <BlockStack gap="150">
                      <Text as="span" fontWeight="bold">🧩 What A Topic Includes</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Each topic can include a <strong>Title</strong>, <strong>Keywords</strong>, an <strong>Auto Reply</strong>, and <strong>Follow-up Suggestions</strong>.
                        The title is required. Keywords and suggestions are optional, but adding them usually makes matching and conversation flow much better.
                      </Text>
                    </BlockStack>
                  </Box>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 4 }}>
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="148px">
                    <BlockStack gap="150">
                      <Text as="span" fontWeight="bold">🔗 Suggestions Only Work If The Topic Exists</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Suggestions are clickable chips shown after a reply. If a customer taps <em>"Browse products"</em>, Reach looks for a topic with that same title.
                        If the topic does not exist, the button will not go anywhere. For every suggestion you add, make sure there is a matching topic.
                      </Text>
                    </BlockStack>
                  </Box>
                </Grid.Cell>
              </Grid>

              <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="400">
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd" fontWeight="semibold">Ways to add knowledge</Text>
                  <Grid>
                    <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 3 }}>
                      <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="140px">
                        <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <ExternalIcon style={{ width: "16px", height: "16px" }} className="theme-icon-accent" />
                            <Text as="span" fontWeight="bold">1. Website Auto-Scan</Text>
                          </InlineStack>
                          <Text as="p" variant="bodySm" tone="subdued">
                            The fastest way to get started. Enter any public page URL, or scan your own website, and Reach will pull in the content automatically. Great for FAQ pages, shipping info, about pages, and other reference content.
                          </Text>
                        </BlockStack>
                      </Box>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 3 }}>
                      <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="140px">
                        <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <Bot size={16} className="text-blue-600" />
                            <Text as="span" fontWeight="bold">2. Add Topics Manually</Text>
                          </InlineStack>
                          <Text as="p" variant="bodySm" tone="subdued">
                            Best for answers you want to control closely, like return policies, pricing, store hours, or contact details. Add a clear title, include plenty of keywords, and write the exact reply you want customers to see.
                          </Text>
                        </BlockStack>
                      </Box>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 3 }}>
                      <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="140px">
                        <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <ClipboardIcon style={{ width: "16px", height: "16px" }} className="theme-icon-success" />
                            <Text as="span" fontWeight="bold">3. Monitor Gaps</Text>
                          </InlineStack>
                          <Text as="p" variant="bodySm" tone="subdued">
                            The <strong>Knowledge Gaps</strong> panel shows questions customers asked that Reach could not match well. Review it regularly. Each missed question is a chance to add a better topic.
                          </Text>
                        </BlockStack>
                      </Box>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 6, md: 6, lg: 3 }}>
                      <Box background="bg-surface-secondary" padding="300" borderRadius="200" minHeight="140px">
                        <BlockStack gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <ClipboardIcon style={{ width: "16px", height: "16px" }} className="theme-icon-accent" />
                            <Text as="span" fontWeight="bold">4. Import / Export</Text>
                          </InlineStack>
                          <Text as="p" variant="bodySm" tone="subdued">
                            Export your topics as <strong>JSON</strong> or <strong>CSV</strong>, make changes, then import them again. Topics with matching titles are updated, and new ones are added. Export an empty template first to see the expected format.
                          </Text>
                        </BlockStack>
                      </Box>
                    </Grid.Cell>
                  </Grid>
                </BlockStack>
              </Box>

              <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" fontWeight="bold">⚡ Seed Greetings - Quick Start</Text>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Customers often open with simple messages like "<em>Hi, Hello, Thanks</em>, or <em>Are you a bot? </em>
                      "<strong> Seed Greetings</strong> adds ready-made topics for these common openers, along with follow-up suggestions to guide the conversation.
                      Use it as a starting point, then create the topics those suggestions point to, like <em>Browse products</em> or <em>Track my order</em>.
                      You can edit these greeting topics in the brain library below after seeding, using the edit button  <Edit2 size={11} /> to better fit your brand voice and offerings.
                      You can run it again anytime. Existing topics are updated, not duplicated.
                    </Text>
                  </BlockStack>
                  <button
                    onClick={onSeedGreetings}
                    style={{
                      background: themeColor,
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      color: "white",
                      fontSize: "13px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Seed Greetings
                  </button>
                </InlineStack>
              </Box>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>

      {showPhase2Tiers && (
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    ✨ Master AI Persona
                  </Text>
                  <Badge tone="magic">AI Sales Rep Only</Badge>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Define how the AI behaves and handles complex queries that fall outside your manual FAQs.
                </Text>
              </BlockStack>
              <Tooltip content={!isRepPlus ? "Available on AI Sales Rep plan" : "Copy to clipboard"}>
                <Button icon={ClipboardIcon} disabled={!isRepPlus} onClick={onCopyMasterPrompt}>
                  Copy Master Prompt
                </Button>
              </Tooltip>
            </InlineStack>
            {!isRepPlus && (
              <Banner tone="info">
                <p>
                  Custom AI Persona &amp; RAG enhancement is available on <strong>AI Sales Rep</strong>. You are currently using the default optimized persona.
                </p>
              </Banner>
            )}
          </BlockStack>
        </Card>
      )}

      <Grid>
        <Grid.Cell columnSpan={{ xs: 6, lg: 6 }}>
          <Card>
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <BlockStack gap="500">
                <Text as="h2" variant="headingMd">
                  Website Auto-Scan
                </Text>
                <Text as="p" tone="subdued">
                  Quickly teach your chatbot by scanning your website. Enter any <strong>public</strong> URL (like FAQs or Shipping Policies) and our system will automatically extract and index the content. For best results, scan pages with rich, unique text content.
                </Text>
                <BlockStack gap="500">
                  <BlockStack gap="400">
                    <TextField
                      label="Page URL"
                      value={scrapeUrl}
                      onChange={setScrapeUrl}
                      placeholder="https://yourstore.com/pages/about"
                      autoComplete="off"
                    />
                    <div className="theme-kb-note">
                      <span style={{ fontSize: 16, lineHeight: 1 }}>🔓</span>
                      <span className="theme-kb-note-text">
                        The URL must be <strong className="theme-kb-note-text-strong">publicly accessible</strong> , password-protected or coming-soon pages can&apos;t be scanned. Temporarily disable your storefront password in Shopify Admin if needed.
                      </span>
                    </div>
                  </BlockStack>
                  <InlineStack align="end">
                    <button
                      onClick={onScanPage}
                      title="Scan Webpage"
                      style={{
                        background: themeColor,
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        cursor: analyticsLoading || isRefreshing ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "white",
                        fontSize: "13px",
                        fontWeight: 600,
                        transition: "background 0.15s",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ display: "flex" }}>
                        <Search size={15} />
                      </span>
                      {loading ? "Scanning..." : "Scan Page"}
                    </button>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </div>
          </Card>
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, lg: 6 }}>
          <Card>
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <BlockStack gap="500">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">
                    Knowledge Gaps (Missed)
                  </Text>
                  <Badge tone="attention">{`${kbGaps.length}`}</Badge>
                </InlineStack>
                <Text as="p" tone="subdued">
                  Questions customers asked that the Chatbot couldn&apos;t answer confidently.
                </Text>

                {kbGaps.length > 0 ? (
                  <BlockStack gap="200">
                    <IndexTable
                      itemCount={kbGaps.length}
                      headings={[{ title: "Query" }, { title: "Hits" }, { title: "Action" }]}
                      selectable={false}
                      pagination={{
                        hasNext: gapPage * gapItemsPerPage < kbGaps.length,
                        hasPrevious: gapPage > 1,
                        onNext: () => setGapPage((prev) => prev + 1),
                        onPrevious: () => setGapPage((prev) => prev - 1),
                        label:
                          kbGaps.length > 0
                            ? `${(gapPage - 1) * gapItemsPerPage + 1}-${Math.min(gapPage * gapItemsPerPage, kbGaps.length)} of ${kbGaps.length}`
                            : "0-0 of 0",
                      }}
                    >
                      {paginatedGapItems.map((gap, index) => (
                        <IndexTable.Row id={gap.id} key={gap.id} position={index}>
                          <IndexTable.Cell>
                            <Text as="span" fontWeight="bold">
                              {gap.question}
                            </Text>
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <Badge tone="info">{`${gap.hitCount || 1}`}</Badge>
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <button
                              onClick={() => onTrainGap(gap)}
                              style={{
                                background: "#f1f5f9",
                                border: "1px solid #e2e8f0",
                                borderRadius: "6px",
                                padding: "3px 8px",
                                cursor: "pointer",
                                color: "#374151",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                lineHeight: "20px",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Train
                            </button>
                          </IndexTable.Cell>
                        </IndexTable.Row>
                      ))}
                    </IndexTable>
                  </BlockStack>
                ) : (
                  <Box padding="400">
                    <div style={{ textAlign: "center" }}>
                      <Text as="p" tone="subdued">
                        No knowledge gaps found. Your Chatbot is doing great!
                      </Text>
                    </div>
                  </Box>
                )}
              </BlockStack>
            </div>
          </Card>
        </Grid.Cell>
      </Grid>

      <Card padding="0">
        <Box padding="400" borderBlockEndWidth="025" borderColor="border">
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <InlineStack gap="400" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Brain Library
                </Text>
                <Badge tone="success">{`${filteredKbItems.length} Trained Topics`}</Badge>
              </InlineStack>
              <InlineStack gap="200">
                <input
                  type="file"
                  id="kb-import-input"
                  style={{ display: "none" }}
                  accept=".json,.csv"
                  onChange={handleImportFile}
                />
                <button
                  onClick={onOpenNewTopicModal}
                  title="Add Topic"
                  style={{
                    background: themeColor,
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    cursor: analyticsLoading || isRefreshing ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: 600,
                    transition: "background 0.15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ display: "flex" }}>
                    <Search size={15} />
                  </span>
                  Add Topic
                </button>
                <button
                  onClick={onSeedGreetings}
                  title="Fill the brain with common greeting inputs (Hi, Hello, Thanks…) mapped to generic sales follow-ups. Safe to run multiple times — existing entries are updated, not duplicated."
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    color: "#374151",
                    fontSize: "13px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Seed Greetings
                </button>
                <button
                  onClick={() => document.getElementById("kb-import-input")?.click()}
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    color: "#374151",
                    fontSize: "13px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Import
                </button>
                <Popover
                  active={showExportPopover}
                  activator={
                    <button
                      onClick={() => setShowExportPopover((value) => !value)}
                      style={{
                        background: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        cursor: "pointer",
                        color: "#374151",
                        fontSize: "13px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Export
                    </button>
                  }
                  autofocusTarget="first-node"
                  onClose={() => setShowExportPopover(false)}
                >
                  <Box
                    padding="300"
                    minWidth="300px"
                    background="bg-surface"
                    borderColor="border"
                    borderWidth="025"
                    borderRadius="300"
                    shadow="300"
                  >
                    <BlockStack gap="300">
                      <Text as="p" variant="bodySm" tone="base">
                        Choose the export format that fits what you want to do next.
                      </Text>
                      <button
                        onClick={() => {
                          setShowExportPopover(false);
                          handleExportJSON();
                        }}
                        style={{
                          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                          border: "1px solid #cbd5e1",
                          borderRadius: "12px",
                          padding: "14px",
                          cursor: "pointer",
                          textAlign: "left",
                          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                        }}
                      >
                        <BlockStack gap="100">
                          <Text as="span" fontWeight="semibold" tone="base">JSON</Text>
                          <Text as="span" variant="bodySm" tone="base">
                            Best for full backups, re-importing later, or working with scripts and AI tools.
                          </Text>
                        </BlockStack>
                      </button>
                      <button
                        onClick={() => {
                          setShowExportPopover(false);
                          handleExportCSV();
                        }}
                        style={{
                          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                          border: "1px solid #cbd5e1",
                          borderRadius: "12px",
                          padding: "14px",
                          cursor: "pointer",
                          textAlign: "left",
                          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                        }}
                      >
                        <BlockStack gap="100">
                          <Text as="span" fontWeight="semibold" tone="base">CSV</Text>
                          <Text as="span" variant="bodySm" tone="base">
                            Best for editing your topics in a spreadsheet before importing them again.
                          </Text>
                        </BlockStack>
                      </button>
                    </BlockStack>
                  </Box>
                </Popover>
                <button
                  onClick={onShowWipeModal}
                  style={{
                    background: "#dc2626",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Wipe Library
                </button>
              </InlineStack>
            </InlineStack>
            <TextField
              label="Search Library"
              labelHidden
              value={kbSearch}
              onChange={setKbSearch}
              placeholder="Search by question, reply, or content..."
              autoComplete="off"
              prefix={<Bot size={16} />}
            />
          </BlockStack>
        </Box>
        <div style={{ overflowX: "auto" }}>
          <IndexTable
            itemCount={filteredKbItems.length}
            headings={[
              { title: "Question" },
              { title: "Auto Reply" },
              { title: "Suggestions" },
              { title: "Reference Content" },
              { title: "Source" },
              { title: "Target URL" },
              { title: "Last Updated" },
              { title: "Actions" },
            ]}
            selectable={false}
            pagination={{
              hasNext: kbPage * kbItemsPerPage < filteredKbItems.length,
              hasPrevious: kbPage > 1,
              onNext: () => setKbPage((prev) => prev + 1),
              onPrevious: () => setKbPage((prev) => prev - 1),
              label:
                filteredKbItems.length > 0
                  ? `${(kbPage - 1) * kbItemsPerPage + 1}-${Math.min(kbPage * kbItemsPerPage, filteredKbItems.length)} of ${filteredKbItems.length}`
                  : "0-0 of 0",
            }}
          >
            {paginatedKbItems.map((item, index) => {
              const question = item.title || item.question || "";
              const reply = item.reply || item.customReply || "";
              const content = item.content || "";
              const suggestions = item.suggestions || "";
              const source = item.source || (item.sourceUrl === "reach:seed:greetings" ? "greeting" : item.sourceUrl === "manual" ? "manual" : item.sourceUrl ? "auto_scan" : "manual");
              const targetUrl = item.targetUrl || (item.sourceUrl && /^https?:\/\//i.test(item.sourceUrl) ? item.sourceUrl : "");
              const isAuto = !reply && !!content;
              const displayReply = reply ? reply : content ? (showPhase2Tiers ? "✨ Auto (RAG)" : "✨ Auto (Ref)") : "—";

              return (
                <IndexTable.Row id={item.id} key={item.id} position={index}>
                  <IndexTable.Cell>
                    <div style={{ maxWidth: "150px" }}>
                      <Tooltip content={question} dismissOnMouseOut>
                        <Text as="span" fontWeight="bold" truncate>
                          {question}
                        </Text>
                      </Tooltip>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ maxWidth: "180px" }}>
                      <Tooltip content={displayReply} dismissOnMouseOut>
                        {isAuto ? (
                          <Badge tone="magic">{showPhase2Tiers ? "✨ Auto (RAG)" : "✨ Auto (Ref)"}</Badge>
                        ) : (
                          <Text as="span" truncate tone={!reply && content ? "subdued" : undefined}>
                            {displayReply}
                          </Text>
                        )}
                      </Tooltip>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ maxWidth: "150px" }}>
                      {suggestions ? (
                        <Text as="span" truncate>
                          {suggestions.split(",").length} replies
                        </Text>
                      ) : (
                        <Text as="span" tone="subdued">
                          —
                        </Text>
                      )}
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ maxWidth: "150px" }}>
                      {content ? (
                        <Text as="span" truncate tone="subdued">
                          {content}
                        </Text>
                      ) : (
                        <Text as="span" tone="subdued">
                          —
                        </Text>
                      )}
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {source === 'greeting' ? (
                      <Badge tone="magic">Greeting</Badge>
                    ) : source === "auto_scan" ? (
                      <Badge tone="info">Auto-Scan</Badge>
                    ) : (
                      <Badge tone="success">Manual</Badge>
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ maxWidth: "180px" }}>
                      {targetUrl ? (
                        <Tooltip content={targetUrl} dismissOnMouseOut>
                          <Text as="span" truncate tone="subdued">
                            {targetUrl}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text as="span" tone="subdued">
                          —
                        </Text>
                      )}
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{new Date(item.updatedAt || item.createdAt || "").toLocaleDateString()}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="200">
                      <button
                        onClick={() => onViewKbItem(item)}
                        title="View"
                        style={{
                          background: "transparent",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px",
                          cursor: "pointer",
                          color: "#64748b",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => onEditKbItem(item)}
                        title="Edit"
                        style={{
                          background: "transparent",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px",
                          cursor: "pointer",
                          color: "#64748b",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => onDeleteKbItem(item.id)}
                        title="Delete"
                        style={{
                          background: "transparent",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px",
                          cursor: "pointer",
                          color: "#f87171",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </InlineStack>
                  </IndexTable.Cell>
                </IndexTable.Row>
              );
            })}
          </IndexTable>
        </div>
      </Card>
    </BlockStack>
  );
}
